import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../../api/client.ts';
import { keys, useMe } from '../../api/hooks.ts';
import { useLogout } from './logout.ts';
import type { Me } from '../../api/types.ts';
import { Button, ErrorNote, Input, Spinner } from '../../components/ui/index.tsx';
import { t, tn } from '../../i18n/index.ts';
import { msg } from '../../i18n/labels.ts';
import { Brand } from '../../layout/Brand.tsx';
import { OAUTH_ERRORS, ProviderButtons, useInviteOnly } from './Providers.tsx';

/** Solo permite redirecciones internas (evita open redirect). */
export function safeNext(next: string | null): string | null {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
}

function AuthFrame({ title, intro, children, footer }: { title: string; intro?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="grid min-h-dvh md:grid-cols-[1fr_minmax(420px,520px)]">
      <section className="relative hidden overflow-hidden bg-pine text-on-pine md:block" aria-hidden>
        <ThreadIllustration />
        <div className="absolute bottom-10 left-10 right-10 max-w-md">
          <p className="font-serif text-[26px] leading-snug">{t('Cada nombre que agregas es un hilo más del mismo tejido.')}</p>
          <p className="mt-3 text-[15px] opacity-75">{t('Tu familia construye su árbol entre todos, con historial de cada cambio.')}</p>
        </div>
      </section>
      <section className="flex flex-col px-5 py-8 md:px-12">
        <Brand />
        <div className="my-auto w-full max-w-sm py-10 max-md:mx-auto md:mx-0">
          <h1 className="text-[30px]">{title}</h1>
          {intro && <p className="mt-2 text-[15.5px] text-ink-soft">{intro}</p>}
          <div className="mt-7">{children}</div>
          {footer && <div className="mt-6 text-[14.5px] text-ink-soft">{footer}</div>}
        </div>
      </section>
    </div>
  );
}

/** Hilos que descienden y se trenzan: abstracción de un pedigrí. */
function ThreadIllustration() {
  const colors = ['var(--cochineal)', 'var(--ochre)', '#7f9be0', '#86b47c'];
  return (
    <svg className="absolute inset-0 size-full opacity-90" viewBox="0 0 600 800" preserveAspectRatio="xMidYMid slice">
      {Array.from({ length: 16 }).map((_, i) => {
        const x = 40 + i * 34;
        const target = 300 + (i % 2 ? 1 : -1) * (8 - Math.floor(i / 2)) * 6;
        return (
          <path
            key={i}
            d={`M${x} -10 C ${x} 220, ${target} 260, ${target} 430 S 300 620, 300 820`}
            stroke={colors[i % 4]}
            strokeWidth={2.2}
            fill="none"
            strokeLinecap="round"
            opacity={0.35 + (i % 4) * 0.12}
          >
            <animate attributeName="stroke-dasharray" from="0 1600" to="1600 0" dur={`${2.2 + (i % 5) * 0.25}s`} fill="freeze" />
          </path>
        );
      })}
    </svg>
  );
}

function useAfterAuth() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return (me: Me) => {
    qc.setQueryData(keys.me, me);
    // La respuesta de entrar/registrarse no trae el estado de verificación ni las cuentas vinculadas.
    void qc.invalidateQueries({ queryKey: keys.me });
    const next = safeNext(params.get('next'));
    if (next) navigate(next, { replace: true });
    else if (me.trees.length === 1) navigate(`/t/${me.trees[0]!.id}`, { replace: true });
    else navigate('/arboles', { replace: true });
  };
}

export function LoginPage() {
  const me = useMe();
  const after = useAfterAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  if (me.data) return <Navigate to={safeNext(params.get('next')) ?? '/'} replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      after(await api.post<Me>('/api/auth/login', { email, password }));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  const next = params.get('next');
  const errorCode = params.get('error');
  const oauthError = errorCode ? (OAUTH_ERRORS[errorCode] ?? OAUTH_ERRORS.oauth_failed) : null;
  return (
    <AuthFrame
      title={t('Entrar')}
      intro={t('Continúa donde dejaste el árbol de tu familia.')}
      footer={
        <>
          {tn('¿Primera vez aquí? {link}', {
            link: <Link className="font-medium text-ink underline underline-offset-4" to={`/registro${next ? `?next=${encodeURIComponent(next)}` : ''}`}>{t('Crea tu cuenta')}</Link>,
          })}
        </>
      }
    >
      {params.get('restablecida') && <p className="mb-5 rounded-2xl bg-surface p-4 text-[15px]">{t('Tu contraseña está lista. Entra con ella.')}</p>}
      {oauthError && <p role="alert" className="mb-5 rounded-2xl border border-danger/30 bg-danger/8 p-4 text-[15px] text-danger">{oauthError}</p>}
      <ProviderButtons next={next} />
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input label={t('Correo')} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label={t('Contraseña')} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <ErrorNote error={error} />
        <Button type="submit" size="lg" loading={loading}>{t('Entrar')}</Button>
        <Link to="/olvide" className="text-center text-[14px] text-ink-soft underline-offset-4 hover:underline">{t('Olvidé mi contraseña')}</Link>
      </form>
    </AuthFrame>
  );
}

export function RegisterPage() {
  const me = useMe();
  const after = useAfterAuth();
  const [params] = useSearchParams();
  const inviteOnly = useInviteOnly();
  const [form, setForm] = useState({ displayName: '', email: '', password: '' });
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  if (me.data) return <Navigate to={safeNext(params.get('next')) ?? '/'} replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      after(await api.post<Me>('/api/auth/register', form));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  const next = params.get('next');
  return (
    <AuthFrame
      title={t('Crea tu cuenta')}
      intro={next?.startsWith('/unirse')
        ? t('Después de crearla te ayudaremos a encontrarte en el árbol.')
        : inviteOnly
          ? t('Con tu cuenta podrás entrar al árbol de tu familia cuando abras su enlace o código QR de invitación.')
          : t('Con tu cuenta puedes crear el árbol de tu familia o unirte a uno.')}
      footer={
        <>
          {tn('¿Ya tienes cuenta? {link}', {
            link: <Link className="font-medium text-ink underline underline-offset-4" to={`/entrar${next ? `?next=${encodeURIComponent(next)}` : ''}`}>{t('Entra')}</Link>,
          })}
        </>
      }
    >
      <ProviderButtons next={next} />
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input label={t('¿Cómo te llamamos?')} autoComplete="name" required maxLength={120} value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
        <Input label={t('Correo')} type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input
          label={t('Contraseña')}
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          hint={t('Mínimo 10 caracteres. Una frase corta es fácil de recordar y difícil de adivinar.')}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <ErrorNote error={error} />
        <Button type="submit" size="lg" loading={loading}>{t('Crear cuenta')}</Button>
      </form>
    </AuthFrame>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/api/auth/forgot', { email });
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <AuthFrame title={t('Recupera tu acceso')} intro={t('Te enviaremos un enlace para crear una contraseña nueva.')} footer={<Link className="underline underline-offset-4" to="/entrar">{t('Volver a entrar')}</Link>}>
      {sent ? (
        <p className="rounded-2xl bg-surface p-4 text-[15px]">{tn('Si existe una cuenta con {email}, recibirás un correo con el enlace. Vence en una hora.', { email: <strong>{email}</strong> })}</p>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Input label={t('Correo')} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <ErrorNote error={error} />
          <Button type="submit" size="lg" loading={loading}>{t('Enviar enlace')}</Button>
        </form>
      )}
    </AuthFrame>
  );
}

export function ResetPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/api/auth/reset', { token, password });
      navigate('/entrar?restablecida=1', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <AuthFrame title={t('Nueva contraseña')} intro={t('Al guardarla se cerrarán tus sesiones abiertas en otros dispositivos.')}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input label={t('Contraseña nueva')} type="password" autoComplete="new-password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} />
        <ErrorNote error={error} />
        <Button type="submit" size="lg" loading={loading}>{t('Guardar contraseña')}</Button>
      </form>
    </AuthFrame>
  );
}

export function VerifyEmailPage() {
  const { token } = useParams();
  const qc = useQueryClient();
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [error, setError] = useState<unknown>(null);
  // Se confirma con un clic (no al cargar): los antivirus de correo que abren enlaces no la consumen.
  const confirm = async () => {
    setState('loading');
    try {
      await api.post('/api/auth/verify-email', { token });
      await qc.invalidateQueries({ queryKey: keys.me });
      setState('done');
    } catch (err) {
      setError(err);
      setState('error');
    }
  };
  return (
    <AuthFrame title={t('Confirma tu correo')} footer={<Link className="underline underline-offset-4" to="/">{t('Ir al inicio')}</Link>}>
      {state === 'done' ? (
        <p className="rounded-2xl bg-surface p-4 text-[15px]">{t('Listo: tu correo quedó confirmado.')}</p>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-[15.5px] text-ink-soft">{t('Confirmar tu correo protege tu cuenta y te permite entrar también con Google u otro proveedor.')}</p>
          <ErrorNote error={error} />
          <Button size="lg" loading={state === 'loading'} onClick={() => void confirm()}>{t('Confirmar mi correo')}</Button>
        </div>
      )}
    </AuthFrame>
  );
}

/** Protege rutas: sin sesión lleva a /entrar conservando el destino; sin correo confirmado, solo deja ver "Mi cuenta". */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const { pathname } = useLocation();
  if (me.isLoading) return <Spinner />;
  if (!me.data) {
    const next = window.location.pathname + window.location.search;
    return <Navigate to={`/entrar?next=${encodeURIComponent(next)}`} replace />;
  }
  if (me.data.user.emailVerified === false && pathname !== '/cuenta') return <ConfirmEmailGate email={me.data.user.email} />;
  return <>{children}</>;
}

/** Pantalla de espera hasta confirmar el correo: reenvía el enlace y se desbloquea al comprobarlo. */
function ConfirmEmailGate({ email }: { email: string }) {
  const logout = useLogout();
  return (
    <AuthFrame
      title={t('Revisa tu correo')}
      intro={tn('Te enviamos un enlace a {email}. Ábrelo para confirmar que el correo es tuyo y empezar con tu árbol.', { email: <strong className="text-ink">{email}</strong> })}
      footer={
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          <Link className="underline underline-offset-4" to="/cuenta">{t('Mi cuenta')}</Link>
          <button type="button" className="underline underline-offset-4" onClick={() => void logout()}>{t('Cerrar sesión')}</button>
        </span>
      }
    >
      <ConfirmEmailActions />
    </AuthFrame>
  );
}

/** Reenviar el enlace o comprobar si ya se confirmó (también dentro del asistente de invitación). */
export function ConfirmEmailActions() {
  const qc = useQueryClient();
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'checking'>('idle');
  const [error, setError] = useState<unknown>(null);
  const resend = async () => {
    setState('sending');
    setError(null);
    try {
      await api.post('/api/auth/verify-email/send');
      setState('sent');
    } catch (err) {
      setError(err);
      setState('idle');
    }
  };
  const check = async () => {
    setState('checking');
    await qc.invalidateQueries({ queryKey: keys.me });
    setState('idle');
    // Si ya está confirmado, RequireAuth deja pasar y esta pantalla desaparece.
    if (!qc.getQueryData<Me>(keys.me)?.user.emailVerified) setError(new Error(msg('Todavía no aparece confirmado. Abre el enlace del correo y pulsa «Confirmar mi correo».')));
  };
  return (
      <div className="flex flex-col gap-4">
        <p className="text-[15px] text-ink-soft">
          {t('Si no lo ves en unos minutos, busca en spam o en «Promociones». El enlace vale 24 horas.')}
        </p>
        {state === 'sent' && <p role="status" className="rounded-2xl bg-ok/10 px-4 py-3 text-[14.5px] text-ok">{t('Enviamos otro enlace. Usa el más reciente.')}</p>}
        <ErrorNote error={error} />
        <Button size="lg" loading={state === 'checking'} onClick={() => void check()}>{t('Ya lo confirmé')}</Button>
        <Button variant="secondary" loading={state === 'sending'} onClick={() => void resend()}>{t('Enviar otro enlace')}</Button>
      </div>
  );
}
